import asyncCallback from '@/framework/logic/utils/asyncCallback';
import { isValidURL } from '@/model/routing/utils';
import Location from '@/ports-frontend/Location';
import MessageBus from '@/ports-frontend/MessageBus';

const EVENT_DEEPLINK_SMARTLINK = 'wh.hybrid.event.deeplink.smartlink';
const EVENT_DEEPLINK_PUSH = 'wh.hybrid.event.deeplink.push';

const isSameDomain = (url: string, currentHostname: string): boolean => {
  try {
    const urlObj = new URL(url);
    return urlObj.hostname === currentHostname;
  } catch (error) {
    console.error('## DeepLink: Failed to check domain for URL:', url, error);
    return false;
  }
};

const processExtractedUrl = (
  url: string | undefined,
  currentHostname: string
): void => {
  if (!url) {
    console.error('## DeepLink: No valid URL extracted from event');
    return;
  }
  if (!isValidURL(url)) {
    console.error('## DeepLink: Invalid URL:', url);
    return;
  }
  if (isSameDomain(url, currentHostname)) {
    const urlObj = new URL(url);
    Location.set(urlObj.pathname + urlObj.search + urlObj.hash);
  }
};

/**
 * Extracts a deep link URL from a push notification payload.
 * 
 * This function parses a JSON string payload, extracts the action URL from the '^d' field,
 * and then retrieves the 'url' parameter from that action URL's query string.
 * 
 * @param pushPayload - The raw push notification payload as a JSON string
 * @returns The extracted URL string, or undefined if extraction fails
 * 
 * @example
 * ```typescript
 * const payload = '{"^d": "whgaming://?action=openURL&url=https://www.ynet.co.il/home/0,7340,L-8,00.html}';
 * const url = extractPushUrl(payload);
 * // Returns: "https://target.com"
 * ```
 * 
 * @remarks
 * The function expects the payload to contain:
 * - A valid JSON structure
 * - A '^d' field containing an action URL
 * - The action URL to have a 'url' query parameter
 * 
 * Errors are logged to console if any step fails during extraction.
 */
const extractPushUrl = (pushPayload: string): string | undefined => {
  try {
    const parsedData = JSON.parse(pushPayload);
    const actionUrl = parsedData['^d'];
    if (!actionUrl) {
      console.error('## DeepLink: Missing ^d field in parsed data:', parsedData);
      return undefined;
    }
    const deepLinkUrl = new URL(actionUrl);
    const url = deepLinkUrl.searchParams.get('url');
    if (!url) {
      console.error('## DeepLink: Invalid or missing URL parameter in action URL:', actionUrl);
      return undefined;
    }
    return url;
  } catch (error) {
    console.error('## DeepLink: Failed to parse push payload:', pushPayload, error);
    return undefined;
  }
};

export const createDeepLinkListener = () => {
  return asyncCallback(async () => {
    try {
      const currentLocation = await Location.get();
      const currentHostname = currentLocation.hostname;

      const unsubscribe = await MessageBus.onSubscribe(
        async ({
          message,
          payload,
        }: {
          message: string;
          payload: { link: string };
        }) => {
          try {
            let extractedUrl: string | undefined;

            if (message === EVENT_DEEPLINK_SMARTLINK) {
              extractedUrl = payload.link;
            } else if (message === EVENT_DEEPLINK_PUSH) {
              extractedUrl = extractPushUrl(payload.link);
            } else {
              console.warn('## DeepLink: No handler found for event type:', message);
              return;
            }

            processExtractedUrl(extractedUrl, currentHostname);

          } catch (error) {
            console.error('## DeepLink: Error processing deep link:', error, {
              message,
              payload,
            });
          }
        },
        [
          EVENT_DEEPLINK_SMARTLINK,
          EVENT_DEEPLINK_PUSH,
        ]
      );

      return unsubscribe;
    } catch (error) {
      console.error('## DeepLink: Error starting deep link listener:', error);
      throw error;
    }
  });
};
